import { z } from 'zod';

import { IsoDateTimeSchema, UuidSchema, BooleanQueryParamSchema } from './common.js';
import { PaginationQuerySchema, paginatedResponseSchema } from './pagination.js';
import { MasterCodeSchema } from './query-types.js';

/**
 * Vehicle Type master (Masters → Vehicle Types). Typed split out of legacy
 * `tbl_MiscellaneousMaster` where `FieldFilter = 'VehicleType'`. Common
 * codes: SEDAN (4-pax sedan), SUV, INNOVA, TEMPO_TRAVELLER (12+),
 * BUS_25 (25-seater coach). Linked from `itinerary_components` for TRANSFER
 * components.
 */

export const VehicleTypeListItemSchema = z.object({
  id: UuidSchema,
  legacyLookupId: z.number().int().nullable(),
  code: z.string(),
  name: z.string(),
  paxCapacity: z.number().int().nullable(),
  isActive: z.boolean(),
  sortOrder: z.number().int(),
  createdAt: IsoDateTimeSchema,
});
export type VehicleTypeListItem = z.infer<typeof VehicleTypeListItemSchema>;

export const VehicleTypeListQuerySchema = PaginationQuerySchema.extend({
  isActive: BooleanQueryParamSchema.optional(),
  includeDeleted: BooleanQueryParamSchema.optional(),
});
export type VehicleTypeListQuery = z.infer<typeof VehicleTypeListQuerySchema>;

export const VehicleTypeListResponseSchema = paginatedResponseSchema(VehicleTypeListItemSchema);
export type VehicleTypeListResponse = z.infer<typeof VehicleTypeListResponseSchema>;

export const VehicleTypeDetailSchema = VehicleTypeListItemSchema.extend({
  updatedAt: IsoDateTimeSchema,
  deletedAt: IsoDateTimeSchema.nullable(),
});
export type VehicleTypeDetail = z.infer<typeof VehicleTypeDetailSchema>;

const VehicleTypeCoreFieldsSchema = z.object({
  code: MasterCodeSchema,
  name: z.string().trim().min(1, 'Name is required').max(120),
  paxCapacity: z.number().int().min(1).max(100).nullable().optional(),
  legacyLookupId: z.number().int().positive().nullable().optional(),
  isActive: z.boolean().optional(),
  sortOrder: z.number().int().min(0).optional(),
});

export const CreateVehicleTypeRequestSchema = VehicleTypeCoreFieldsSchema;
export type CreateVehicleTypeRequest = z.infer<typeof CreateVehicleTypeRequestSchema>;

export const UpdateVehicleTypeRequestSchema = VehicleTypeCoreFieldsSchema.partial();
export type UpdateVehicleTypeRequest = z.infer<typeof UpdateVehicleTypeRequestSchema>;
